<template>
  <div class="interpretation-manager">
    <h1>Manajemen Interpretasi Skor</h1>
    <p>Atur teks dan kategori (A, B, C) yang akan muncul di laporan berdasarkan rentang skor untuk setiap sub-aspek.</p>

    <div class="card">
      <h2>Pilih Sub-Aspek</h2>
      <select v-model="selectedSubAspectId" @change="fetchRules" class="main-select">
        <option :value="null">-- Pilih Sub-Aspek untuk Dikelola --</option>
        <optgroup v-for="group in groupedSubAspects" :key="group.aspect_name" :label="group.aspect_name">
          <option v-for="sub in group.sub_aspects" :key="sub.id" :value="sub.id">
            {{ sub.name }}
          </option>
        </optgroup>
      </select>
    </div>

    <div v-if="selectedSubAspectId">
      <div class="card">
        <h2>Aturan untuk: {{ selectedSubAspectName }}</h2>
        <div v-if="isLoadingRules">Memuat aturan...</div>
        <table v-else-if="rules.length > 0">
          <thead>
            <tr>
              <th>Skor Min</th>
              <th>Skor Max</th>
              <th>Kategori</th>
              <th>Teks Interpretasi</th>
              <th>Aksi</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="rule in rules" :key="rule.id">
              <td>{{ rule.min_score }}</td>
              <td>{{ rule.max_score }}</td>
              <td><strong>{{ rule.category }}</strong></td>
              <td class="interpretation-text">{{ rule.interpretation_text }}</td>
              <td>
                <button @click="editRule(rule)" class="edit-btn">Edit</button>
                <button @click="deleteRule(rule.id)" class="delete-btn">Hapus</button>
              </td>
            </tr>
          </tbody>
        </table>
        <p v-else>Belum ada aturan untuk sub-aspek ini.</p>
      </div>

      <div class="card">
        <h2>{{ isEditing ? 'Edit Aturan' : 'Tambah Aturan Baru' }}</h2>
        <form @submit.prevent="isEditing ? updateRule() : addRule()" class="add-form">
          <input v-model.number="newRule.min_score" type="number" placeholder="Skor Min" required />
          <input v-model.number="newRule.max_score" type="number" placeholder="Skor Max" required />
          <input v-model="newRule.category" type="text" placeholder="Kategori (A/B/C)" required />
          <textarea v-model="newRule.interpretation_text" placeholder="Teks Interpretasi" required rows="3"></textarea>
          <div class="form-actions">
            <button type="submit" class="submit-btn">{{ isEditing ? 'Simpan' : 'Tambah' }}</button>
            <button v-if="isEditing" type="button" @click="cancelEdit" class="cancel-btn">Batal</button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue';
import apiClient from '@/services/api.js';

const allSubAspects = ref([]);
const selectedSubAspectId = ref(null);
const rules = ref([]);
const isLoadingRules = ref(false);

const isEditing = ref(false);
const editingRuleId = ref(null);

const newRule = ref({
  min_score: 0,
  max_score: 10,
  category: '',
  interpretation_text: '',
});

const groupedSubAspects = computed(() => {
  const groups = {};
  allSubAspects.value.forEach(sub => {
    const aspectName = sub.aspect?.name || 'Lainnya';
    if (!groups[aspectName]) {
      groups[aspectName] = { aspect_name: aspectName, sub_aspects: [] };
    }
    groups[aspectName].sub_aspects.push(sub);
  });
  return Object.values(groups);
});

const selectedSubAspectName = computed(() => {
  const sub = allSubAspects.value.find(s => s.id === selectedSubAspectId.value);
  return sub ? sub.name : '';
});

const fetchAllSubAspects = async () => {
  try {
    const response = await apiClient.get('/admin/sub-aspects/');
    allSubAspects.value = response.data;
  } catch (err) { console.error(err); }
};

const fetchRules = async () => {
  isEditing.value = false;
  editingRuleId.value = null;
  newRule.value = { min_score: 0, max_score: 10, category: '', interpretation_text: '' };

  if (!selectedSubAspectId.value) {
    rules.value = [];
    return;
  }
  isLoadingRules.value = true;
  try {
    // API endpoint ini perlu diubah di backend
    const response = await apiClient.get(`/admin/sub-aspects/${selectedSubAspectId.value}/interpretation-rules`);
    rules.value = response.data;
  } catch (err) {
    console.error(err);
    rules.value = [];
  } finally {
    isLoadingRules.value = false;
  }
};

const addRule = async () => {
  try {
    const payload = { ...newRule.value, sub_aspect_id: selectedSubAspectId.value };
    await apiClient.post('/admin/interpretation-rules/', payload);
    newRule.value = { min_score: 0, max_score: 10, category: '', interpretation_text: '' };
    await fetchRules();
  } catch (err) { alert("Gagal menambah aturan."); }
};

const deleteRule = async (ruleId) => {
  if (window.confirm("Yakin ingin menghapus aturan ini?")) {
    try {
      await apiClient.delete(`/admin/interpretation-rules/${ruleId}`);
      await fetchRules();
    } catch (err) { alert("Gagal menghapus aturan."); }
  }
};

const editRule = (rule) => {
  isEditing.value = true;
  editingRuleId.value = rule.id;
  newRule.value = {
    min_score: rule.min_score,
    max_score: rule.max_score,
    category: rule.category,
    interpretation_text: rule.interpretation_text,
  };
};

const updateRule = async () => {
  try {
    const payload = { ...newRule.value, sub_aspect_id: selectedSubAspectId.value };
    await apiClient.put(`/admin/interpretation-rules/${editingRuleId.value}`, payload);
    cancelEdit();
    await fetchRules();
  } catch (err) { alert("Gagal mengupdate aturan."); }
};

const cancelEdit = () => {
  isEditing.value = false;
  editingRuleId.value = null;
  newRule.value = { min_score: 0, max_score: 10, category: '', interpretation_text: '' };
};

onMounted(fetchAllSubAspects);
</script>

<style scoped>
.interpretation-manager { max-width: 960px; margin: auto; padding: 20px; }
.card { margin-top: 20px; padding: 20px; border-radius: 8px; background: #fff; box-shadow: 0 2px 4px rgba(0,0,0,0.05); }
select { width: 100%; padding: 10px; }
table { width: 100%; }
.interpretation-text { white-space: pre-wrap; }
.add-form { display: flex; gap: 10px; align-items: flex-start; }
.add-form textarea { flex-grow: 1; }
.edit-btn { background-color: #f39c12; color: white; border: none; padding: 5px 10px; border-radius: 4px; margin-right: 5px; cursor: pointer; }
.delete-btn { background-color: #e74c3c; color: white; border: none; padding: 5px 10px; border-radius: 4px; cursor: pointer; }
.submit-btn { background-color: #2ecc71; color: white; border: none; padding: 8px 12px; border-radius: 4px; cursor: pointer; }
.cancel-btn { background-color: #95a5a6; color: white; border: none; padding: 8px 12px; border-radius: 4px; cursor: pointer; }
.form-actions { display: flex; flex-direction: column; gap: 5px; }
.main-select {
  padding: 12px;
  font-size: 1.1em;
  margin-bottom: 20px;
}
</style>